import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ChangePathContext } from '../App';

export const useUnsavedChanges = (hasChanges = false, onDirectClose) => {
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const isDirty = Boolean(hasChanges);
  const context = useContext(ChangePathContext);
  const setSaveChanges = context?.setSaveChanges;

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const showUnsavedModalRef = useRef(showUnsavedModal);
  showUnsavedModalRef.current = showUnsavedModal;

  const onDirectCloseRef = useRef(onDirectClose);
  onDirectCloseRef.current = onDirectClose;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        if (showUnsavedModalRef.current) {
          // If the unsaved changes popup is already open, pressing Escape closes the popup (stays on page)
          setShowUnsavedModal(false);
          return;
        }

        // If changes exist, always show unsaved modal on Escape
        if (isDirtyRef.current) {
          setShowUnsavedModal(true);
        } else if (onDirectCloseRef.current) {
          onDirectCloseRef.current();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (setSaveChanges) {
      setSaveChanges((prev) => ({
        ...prev,
        isUnsaveChanges: isDirty,
      }));
    }
    return () => {
      if (setSaveChanges) {
        setSaveChanges((prev) => ({
          ...prev,
          isUnsaveChanges: false,
        }));
      }
    };
  }, [isDirty, setSaveChanges]);

  const handleRequestClose = useCallback(
    (customClose) => {
      if (isDirtyRef.current) {
        setShowUnsavedModal(true);
      } else {
        const closeFn = typeof customClose === 'function' ? customClose : onDirectCloseRef.current;
        if (typeof closeFn === 'function') closeFn();
      }
    },
    [],
  );

  const handleConfirmLeave = useCallback(
    (customLeave) => {
      setShowUnsavedModal(false);
      if (setSaveChanges) {
        setSaveChanges((prev) => ({
          ...prev,
          isUnsaveChanges: false,
          showChangesPopup: false,
        }));
      }
      const leaveFn = typeof customLeave === 'function' ? customLeave : onDirectCloseRef.current;
      if (typeof leaveFn === 'function') leaveFn();
    },
    [setSaveChanges],
  );

  const handleStay = useCallback(() => {
    setShowUnsavedModal(false);
  }, []);

  return {
    showUnsavedModal,
    setShowUnsavedModal,
    handleRequestClose,
    handleConfirmLeave,
    handleStay,
  };
};

export default useUnsavedChanges;
